import { createFileRoute } from "@tanstack/react-router"
import { Calendar, CreditCard, Eye, EyeOff, Lock, Mail, Search, User } from "lucide-react"
import { useState } from "react"
import { Input } from "@/components/ui/input"
import { PageHeader } from "@/components/ui/PageHeader"

export const Route = createFileRoute("/design/input-showcase")({
  component: InputShowcase,
})

function InputShowcase() {
  const [showPassword, setShowPassword] = useState(false)
  const [values, setValues] = useState({
    default: "",
    withLabel: "",
    withHelper: "",
    withError: "",
    password: "",
    search: "",
    disabled: "",
    required: "",
    leadingIcon: "",
    trailingIcon: "",
  })

  return (
    <div className="bg-background min-h-screen p-8">
      <div className="mx-auto max-w-4xl">
        <PageHeader
          title="Input Field Component"
          description="A comprehensive input component following the Shelfi design system"
        />

        <div className="mt-8 space-y-12">
          {/* Basic Inputs */}
          <section>
            <h2 className="text-brand-darkblue-500 dark:text-brand-cyan-150 mb-6 text-2xl font-semibold">
              Basic Input States
            </h2>
            <div className="space-y-6">
              {/* Default Input */}
              <div>
                <h3 className="mb-3 text-lg font-medium">Default Input</h3>
                <Input
                  placeholder="Enter text..."
                  value={values.default}
                  onChange={(e) => setValues({ ...values, default: e.target.value })}
                />
              </div>

              {/* Input with Label */}
              <div>
                <h3 className="mb-3 text-lg font-medium">With Label</h3>
                <Input
                  label="Email Address"
                  placeholder="you@example.com"
                  type="email"
                  value={values.withLabel}
                  onChange={(e) => setValues({ ...values, withLabel: e.target.value })}
                />
              </div>

              {/* Input with Helper Text */}
              <div>
                <h3 className="mb-3 text-lg font-medium">With Helper Text</h3>
                <Input
                  label="Username"
                  helperText="Choose a unique username that others can use to find you"
                  placeholder="johndoe"
                  value={values.withHelper}
                  onChange={(e) => setValues({ ...values, withHelper: e.target.value })}
                />
              </div>

              {/* Input with Error */}
              <div>
                <h3 className="mb-3 text-lg font-medium">With Error State</h3>
                <Input
                  label="Email"
                  error="Please enter a valid email address"
                  placeholder="you@example.com"
                  type="email"
                  value={values.withError}
                  onChange={(e) => setValues({ ...values, withError: e.target.value })}
                />
              </div>

              {/* Required Input */}
              <div>
                <h3 className="mb-3 text-lg font-medium">Required Field</h3>
                <Input
                  label="Full Name"
                  required
                  placeholder="John Doe"
                  value={values.required}
                  onChange={(e) => setValues({ ...values, required: e.target.value })}
                />
              </div>

              {/* Disabled Input */}
              <div>
                <h3 className="mb-3 text-lg font-medium">Disabled State</h3>
                <Input
                  label="Disabled Field"
                  disabled
                  placeholder="This field is disabled"
                  value="Cannot edit this"
                />
              </div>
            </div>
          </section>

          {/* Inputs with Icons */}
          <section>
            <h2 className="text-brand-darkblue-500 dark:text-brand-cyan-150 mb-6 text-2xl font-semibold">
              Inputs with Icons
            </h2>
            <div className="space-y-6">
              {/* Search Input */}
              <div>
                <h3 className="mb-3 text-lg font-medium">Search Input</h3>
                <Input
                  label="Search"
                  leadingIcon={<Search className="h-5 w-5" />}
                  placeholder="Search for items..."
                  value={values.search}
                  onChange={(e) => setValues({ ...values, search: e.target.value })}
                />
              </div>

              {/* Email with Icon */}
              <div>
                <h3 className="mb-3 text-lg font-medium">Email with Icon</h3>
                <Input
                  label="Email Address"
                  leadingIcon={<Mail className="h-5 w-5" />}
                  placeholder="you@example.com"
                  type="email"
                  value={values.leadingIcon}
                  onChange={(e) => setValues({ ...values, leadingIcon: e.target.value })}
                />
              </div>

              {/* Password with Toggle */}
              <div>
                <h3 className="mb-3 text-lg font-medium">Password with Toggle</h3>
                <Input
                  label="Password"
                  leadingIcon={<Lock className="h-5 w-5" />}
                  trailingIcon={
                    showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />
                  }
                  onTrailingIconClick={() => setShowPassword(!showPassword)}
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={values.password}
                  onChange={(e) => setValues({ ...values, password: e.target.value })}
                />
              </div>

              {/* Input with Both Icons */}
              <div>
                <h3 className="mb-3 text-lg font-medium">With Both Icons</h3>
                <Input
                  label="Profile URL"
                  leadingIcon={<User className="h-5 w-5" />}
                  trailingIcon={<Calendar className="h-5 w-5" />}
                  placeholder="shelfi.com/username"
                  value={values.trailingIcon}
                  onChange={(e) => setValues({ ...values, trailingIcon: e.target.value })}
                  helperText="This will be your public profile URL"
                />
              </div>

              {/* Error State with Icon */}
              <div>
                <h3 className="mb-3 text-lg font-medium">Error State with Icon</h3>
                <Input
                  label="Credit Card"
                  leadingIcon={<CreditCard className="h-5 w-5" />}
                  error="Invalid card number"
                  placeholder="1234 5678 9012 3456"
                  value="1234"
                />
              </div>
            </div>
          </section>

          {/* Backward Compatibility */}
          <section>
            <h2 className="text-brand-darkblue-500 dark:text-brand-cyan-150 mb-6 text-2xl font-semibold">
              Backward Compatibility Mode
            </h2>
            <p className="mb-4 text-sm text-neutral-600 dark:text-neutral-400">
              When used without the new props (label, helperText, error, icons), the Input component
              maintains backward compatibility with the old implementation.
            </p>
            <div className="space-y-6">
              <div>
                <h3 className="mb-3 text-lg font-medium">Simple Input (Old API)</h3>
                <Input placeholder="This uses the old API" className="max-w-md" />
              </div>
            </div>
          </section>

          {/* Design Specifications */}
          <section className="rounded-lg bg-neutral-50 p-6 dark:bg-neutral-900">
            <h2 className="mb-4 text-xl font-semibold">Design Specifications</h2>
            <div className="grid gap-6 text-sm md:grid-cols-2">
              <div>
                <h3 className="mb-2 font-medium">Dimensions</h3>
                <ul className="space-y-1 text-neutral-600 dark:text-neutral-400">
                  <li>• Height: 44px (fixed)</li>
                  <li>• Horizontal padding: 24px</li>
                  <li>• Icon size: 24x24px</li>
                  <li>• Border width: 1px (2px on focus)</li>
                </ul>
              </div>
              <div>
                <h3 className="mb-2 font-medium">States</h3>
                <ul className="space-y-1 text-neutral-600 dark:text-neutral-400">
                  <li>• Default: Neutral border</li>
                  <li>• Hover: Darker border</li>
                  <li>• Focus: 2px blue border</li>
                  <li>• Error: Red border with triangle icon</li>
                  <li>• Disabled: Gray background</li>
                </ul>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  )
}
